/**
 * ReferenceSinglePicker — inline single-select из справочника (по id).
 *
 * Generic-обёртка над InlinePicker для полей вида "Регион продаж", "Сегмент (single)" и т.п.
 * Consumer передаёт `fetchOptions` (одноразовая загрузка справочника) и текстовое значение для display.
 *
 * Для специфичных кейсов (с навигацией, аватарками) используйте отдельные компоненты
 * (PartnerPicker, UserPicker) — этот — для общих справочников.
 */
import { useEffect, useMemo, useState } from 'react';
import { Box, Group, ScrollArea, Stack, Text, TextInput, type MantineSize } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { InlinePicker } from './InlinePicker';

export interface ReferenceOption {
  id: number;
  label: string;
  hint?: string | null;
}

export interface ReferenceSinglePickerProps {
  /** Выбранный id или null. */
  value: number | null;
  /** Колбек с новым id (или null). */
  onChange: (id: number | null) => void;
  /** Лейбл выбранного значения для display (приходит из родителя через JOIN). */
  selectedLabel?: string | null;
  /** Одноразовая загрузка справочника при первом открытии popover. */
  fetchOptions: () => Promise<ReferenceOption[]>;
  /** Плейсхолдер если ничего не выбрано. */
  placeholder?: string;
  /** Лейбл "пустой" строки (null-выбор). */
  emptyLabel?: string;
  /** Если false — display только. */
  canEdit?: boolean;
  /** Цвет ссылки display (если задан — выбранный label рендерится этим цветом). */
  displayColor?: string;
  /** Размер display. */
  size?: MantineSize;
  /** Размер popover content. */
  inputSize?: MantineSize;
  /** Ширина popover. */
  width?: number;
}

type RowProps = {
  label: string;
  hint?: string | null;
  selected: boolean;
  onClick: () => void;
  size: MantineSize;
};

function OptionRow({ label, hint, selected, onClick, size }: RowProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'pointer',
        padding: '6px 8px',
        borderRadius: 4,
        background: hovered ? 'var(--mantine-color-gray-1)' : 'transparent',
        transition: 'background 0.1s ease',
      }}
    >
      <Group gap={8} wrap='nowrap' justify='space-between'>
        <Box style={{ minWidth: 0 }}>
          <Text
            size={size}
            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {label}
          </Text>
          {hint ? (
            <Text
              size='xs'
              c='dimmed'
              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {hint}
            </Text>
          ) : null}
        </Box>
        {selected ? <IconCheck size={14} color='var(--mantine-color-blue-6)' /> : null}
      </Group>
    </Box>
  );
}

export function ReferenceSinglePicker({
  value,
  onChange,
  selectedLabel,
  fetchOptions,
  placeholder = 'Не указан',
  emptyLabel = 'Не указан',
  canEdit = true,
  displayColor,
  size = 'sm',
  inputSize = 'sm',
  width = 300,
}: ReferenceSinglePickerProps) {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<ReferenceOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!opened || options.length > 0) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const rows = await fetchOptions();
        if (!cancelled) setOptions(rows ?? []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, options.length, fetchOptions]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter(
      o => o.label.toLowerCase().includes(q) || o.hint?.toLowerCase().includes(q)
    );
  }, [options, search]);

  const select = (id: number | null) => {
    onChange(id);
    setOpened(false);
    setSearch('');
  };

  const display = (
    <Text
      size={size}
      c={selectedLabel ? displayColor : 'dimmed'}
      style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
    >
      {selectedLabel || placeholder}
    </Text>
  );

  return (
    <InlinePicker
      display={display}
      opened={opened}
      onOpenChange={setOpened}
      canEdit={canEdit}
      width={width}
    >
      <Stack gap={6}>
        <TextInput
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
          placeholder='Поиск...'
          size={inputSize}
          autoFocus
        />
        <ScrollArea.Autosize mah={300} type='hover' scrollbarSize={6} scrollbars='y'>
          <Stack gap={2}>
            <OptionRow
              label={emptyLabel}
              selected={value === null}
              onClick={() => select(null)}
              size={inputSize}
            />
            {filtered.map(o => (
              <OptionRow
                key={o.id}
                label={o.label}
                hint={o.hint}
                selected={o.id === value}
                onClick={() => select(o.id)}
                size={inputSize}
              />
            ))}
            {loading ? (
              <Text size='xs' c='dimmed' ta='center' py='xs'>
                Загрузка...
              </Text>
            ) : null}
            {!loading && filtered.length === 0 && search ? (
              <Text size='xs' c='dimmed' ta='center' py='xs'>
                Ничего не найдено
              </Text>
            ) : null}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
    </InlinePicker>
  );
}
